import React, { ReactElement, useEffect, useMemo, useReducer, useState } from 'react'
import { PAY_BASE_URL, RADOM_COLORS } from '../../util/Constants'
import Radom, { InputFieldDataType, IPaymentLink, IProduct } from '../../state/Radom'
import { useNavigate, useSearchParams } from 'react-router-dom'
import PaymentLinkForm from '../../components/PaymentLinkForm'
import Preview from '../../components/Preview'
import { errorToast, successToast } from '../../util/Util'
import NavbarController from '../../state/NavbarController'

const qaTimestamp = '2026-04-21T00:00:00.000Z'

const qaProducts: IProduct[] = [
  {
    id: 'qa-product-launch-plan',
    name: 'Launch plan',
    description: 'One-time onboarding and payment setup package.',
    addOns: [],
    chargingIntervalSeconds: 0,
    currency: 'GBP',
    price: 100,
    quantity: 1,
    sendSubscriptionEmails: true,
    createdAt: qaTimestamp,
    updatedAt: qaTimestamp,
    imageUrl: '',
    isArchived: false
  },
  {
    id: 'qa-product-priority-support',
    name: 'Priority support',
    description: 'Monthly priority support for merchant operations.',
    addOns: [],
    chargingIntervalSeconds: 2592000,
    currency: 'GBP',
    price: 49,
    quantity: 1,
    sendSubscriptionEmails: true,
    createdAt: qaTimestamp,
    updatedAt: qaTimestamp,
    imageUrl: '',
    isArchived: false
  }
]

export const emptyPaymentLink: IPaymentLink = {
  id: '',
  url: '',
  products: [],
  gateway: {
    managed: {
      methods: []
    }
  },
  customizations: {
    leftPanelColor: RADOM_COLORS.GRAY_MED,
    primaryButtonColor: RADOM_COLORS.BLACK,
    slantedEdge: false,
    allowDiscountCodes: false
  },
  sendEmailReceipt: false,
  createdAt: new Date(),
  updatedAt: new Date(),
  cancelUrl: '',
  successUrl: '',
  inputFields: [],
  chargeCustomerNetworkFee: true
}

export const CreatePaymentLink = (): ReactElement => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isQaSeed = searchParams.get('qa') === '1'

  const [paymentLink, setPaymentLink] = useReducer(
    (p: IPaymentLink, n: Partial<IPaymentLink>) => {
      return { ...p, ...n }
    }, emptyPaymentLink)

  const [isSubmitting, setIsSubmitting] = useState(false)

  const previewData = useMemo(() => {
    const sellerName = Radom.sellerProfile?.name ?? (isQaSeed ? 'Radom QA Studio' : undefined)

    return Buffer.from(JSON.stringify({
      ...paymentLink,
      sellerName,
      sellerLogoUrl: Radom.sellerProfile?.logoUrl,
      inputFields: []
    }), 'utf8').toString('base64')
  }, [isQaSeed, paymentLink, paymentLink.inputFields])
  const [previewWidth, setPreviewWidth] = useState('90%')

  const handleCreatePaymentLink = async (): Promise<void> => {
    try {
      setIsSubmitting(true)
      await Radom.createPaymentLink({
        products: paymentLink.products.map(p => p.id),
        gateway: paymentLink.gateway,
        customizations: paymentLink.customizations,
        cancelUrl: paymentLink.cancelUrl,
        successUrl: paymentLink.successUrl,
        inputFields: paymentLink.inputFields,
        sendEmailReceipt: paymentLink.sendEmailReceipt,
        chargeCustomerNetworkFee: paymentLink.chargeCustomerNetworkFee
      })
      setIsSubmitting(false)

      successToast('Successfully created payment link')
      navigate('/payment_links')
    } catch (err) {
      setIsSubmitting(false)
      console.error(err)
      errorToast(err.response
        ? err.response.data.error || err.response.data
        : err.message)
    }
  }

  useEffect(() => {
    NavbarController.setFullscreen(true)
    return () => NavbarController.setFullscreen(false)
  }, [])

  useEffect(() => {
    if (!isQaSeed) {
      return
    }

    setPaymentLink({
      ...emptyPaymentLink,
      id: 'qa-payment-link-designer',
      url: `${PAY_BASE_URL}/payment_link/qa-payment-link-designer`,
      products: qaProducts,
      gateway: {
        managed: {
          methods: [
            { network: 'Ethereum' },
            { network: 'Base' },
            { network: 'Ethereum', token: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48' },
            { network: 'Base', token: '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913' },
            { network: 'Solana', token: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v' }
          ]
        }
      },
      customizations: {
        ...emptyPaymentLink.customizations,
        leftPanelColor: 'rgba(0,0,0,1)',
        primaryButtonColor: '#0a2540',
        allowDiscountCodes: true
      },
      sendEmailReceipt: true,
      inputFields: [
        { dataType: InputFieldDataType.String, inputLabel: 'Name', isRequired: true },
        { dataType: InputFieldDataType.Email, inputLabel: 'Email address', isRequired: true }
      ],
      chargeCustomerNetworkFee: true,
      successUrl: 'https://radom.com/checkout/success',
      cancelUrl: 'https://radom.com/checkout/cancel',
      createdAt: new Date(qaTimestamp),
      updatedAt: new Date(qaTimestamp)
    })
  }, [isQaSeed])

  return (
    <div style={{ display: 'flex', height: '100vh', position: 'relative' }}>
      <PaymentLinkForm
        paymentLink={paymentLink}
        skipRemoteData={isQaSeed}
        onFormSubmit={handleCreatePaymentLink}
        onFormChange={setPaymentLink}
        isSubmitting={isSubmitting}
      />
      <Preview
        iframeLink={`${PAY_BASE_URL}/preview/pay#${previewData}`}
        previewWidth={previewWidth}
        onWidthChange={setPreviewWidth}
        iframeTitle='pay.radom.com/payment-link'
      />
    </div>
  )
}
